import { useCallback, useEffect, useRef, useState } from "react";
import SVG from "../../../Components/SVG";
import { useNavigate } from "react-router-dom";
import TableLoading from "../../../Components/Loader/TableLoading";
import ImageUploadPreviewGrid from "../../../Components/UploadPreview/ImageUploadPreviewGrid";
import { API_BASE_URL } from "../../../config/api";
import useInfinitePaginated, { mergeBuckets } from "../../../hooks/useInfinitePaginated";

const KYC_UPLOAD_URL = `${API_BASE_URL}auth/kyc/submit/`;
const KYC_FILE_ACCEPT = ".jpg,.jpeg,.png";
const EMPTY_KYC = { pending: [], rejected: [], approved: [] };
const mergeKyc = mergeBuckets(["pending", "rejected", "approved"], (item) => item?.id ?? item?.uuid ?? `${item?.kyc_type_name}-${item?.created_at}`);

const getErrorMessageFromPayload = (payload, fallbackText) => {
  if (!payload) return fallbackText;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
};

export default function KYCCard() {
  const navigate = useNavigate();
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [formError, setFormError] = useState("");
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [idUploadProgress, setIdUploadProgress] = useState(null);
  const [selfieUploadProgress, setSelfieUploadProgress] = useState(null);
  const [activeUploadField, setActiveUploadField] = useState(null);

  const [profileLoading, setProfileLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const uploadRequestRef = useRef(null);
  const {
    initialLoading: kycInitialLoading,
    error: kycError,
    summary: kycSummary,
    retry: retryKyc,
    reload: reloadKyc,
  } = useInfinitePaginated({
    endpoint: `${API_BASE_URL}auth/kyc/list/`,
    navigate,
    initialData: EMPTY_KYC,
    select: (response) => response?.results || EMPTY_KYC,
    merge: mergeKyc,
  });
  const identityStatus = kycSummary?.identity_status ?? null;
  const selfieStatus = kycSummary?.identity_with_selfie_status ?? null;
  const hasKycSummary =
    kycSummary != null &&
    Object.prototype.hasOwnProperty.call(kycSummary, "identity_status") &&
    Object.prototype.hasOwnProperty.call(kycSummary, "identity_with_selfie_status");
  const acceptedKycStatuses = ["approved", "pending"];
  const showButton = !(
    acceptedKycStatuses.includes(identityStatus) &&
    acceptedKycStatuses.includes(selfieStatus)
  );

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(
      `${API_BASE_URL}auth/trader/jwt/refresh/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: localStorage.getItem("refresh"),
        }),
      },
    );

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      headers.set("Authorization", `Bearer ${localStorage.getItem("access")}`);

      let response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status !== 401) return response;

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) return null;

      const retryHeaders = new Headers(options.headers || {});
      if (
        !(options.body instanceof FormData) &&
        !retryHeaders.has("Content-Type")
      ) {
        retryHeaders.set("Content-Type", "application/json");
      }
      retryHeaders.set(
        "Authorization",
        `Bearer ${localStorage.getItem("access")}`,
      );

      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });

      return response;
    },
    [refreshAccessToken],
  );

  const authUpload = useCallback(
    async (url, { method = "POST", body, onProgress } = {}) => {
      const sendRequest = (accessToken) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          uploadRequestRef.current = xhr;
          xhr.open(method, url);
          xhr.responseType = "text";
          xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

          if (xhr.upload) {
            xhr.upload.onprogress = (event) => {
              if (!event.lengthComputable) return;
              if (typeof onProgress === "function") {
                onProgress(Math.round((event.loaded / event.total) * 100));
              }
            };
          }

          xhr.onload = () => {
            uploadRequestRef.current = null;
            let payload = null;

            if (
              typeof xhr.responseText === "string" &&
              xhr.responseText.trim()
            ) {
              try {
                payload = JSON.parse(xhr.responseText);
              } catch (error) {
                payload = xhr.responseText;
              }
            }

            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              payload,
            });
          };

          xhr.onerror = () => {
            uploadRequestRef.current = null;
            reject(new Error("upload-request-failed"));
          };
          xhr.onabort = () => {
            uploadRequestRef.current = null;
            reject(new Error("upload-request-aborted"));
          };

          xhr.send(body);
        });
      };

      const currentAccessToken = localStorage.getItem("access");
      let response = await sendRequest(currentAccessToken);

      if (response.status !== 401) return response;

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) return null;

      response = await sendRequest(nextAccessToken);
      return response;
    },
    [refreshAccessToken],
  );

  const fetchAccountData = useCallback(async () => {
    try {
      const response = await authFetch(
        `${API_BASE_URL}auth/trader/info/`,
        {
          method: "GET",
        },
      );
      if (!response) return;

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Account Data:", data);
      setAccountData(data);
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  }, [authFetch]);

  useEffect(() => {
    let mounted = true;
    setProfileLoading(true);
    fetchAccountData().finally(() => {
      if (mounted) setProfileLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [fetchAccountData]);

  useEffect(() => {
    return () => {
      if (uploadRequestRef.current) {
        uploadRequestRef.current.abort();
      }
    };
  }, []);

  const handleFileChange = (event, setter, progressSetter) => {
    const nextFile = event?.target?.files?.[0] || null;
    setter(nextFile);
    progressSetter(null);
    setFormError("");
    if (event?.target) {
      event.target.value = "";
    }
  };

  const handleCancelUpload = () => {
    if (uploadRequestRef.current) {
      uploadRequestRef.current.abort();
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (kycSubmitting) return;
    if (!idFile || !selfieFile) {
      setFormError("هر دو فایل الزامی است.");
      return;
    }

    setKycSubmitting(true);
    setFormError("");
    setActiveUploadField("id");
    setIdUploadProgress(0);
    setSelfieUploadProgress(null);

    try {
      const idForm = new FormData();
      idForm.append("kyc_type", 1);
      idForm.append("documents", idFile);
      let response = await authUpload(KYC_UPLOAD_URL, {
        method: "POST",
        body: idForm,
        onProgress: setIdUploadProgress,
      });

      if (!response) return;
      if (!response.ok) {
        setFormError(
          getErrorMessageFromPayload(
            response.payload,
            "آپلود مدرک شناسایی با خطا مواجه شد.",
          ),
        );
        return;
      }

      setIdUploadProgress(100);
      setActiveUploadField("selfie");
      setSelfieUploadProgress(0);

      const selfieForm = new FormData();
      selfieForm.append("kyc_type", 2);
      selfieForm.append("documents", selfieFile);
      response = await authUpload(KYC_UPLOAD_URL, {
        method: "POST",
        body: selfieForm,
        onProgress: setSelfieUploadProgress,
      });

      if (!response) return;
      if (!response.ok) {
        setFormError(
          getErrorMessageFromPayload(
            response.payload,
            "آپلود تصویر سلفی با خطا مواجه شد.",
          ),
        );
        return;
      }

      setSelfieUploadProgress(100);
      setIdFile(null);
      setSelfieFile(null);
      reloadKyc();
    } catch (error) {
      if (error?.message === "upload-request-aborted") {
        setFormError("آپلود مدارک لغو شد.");
      } else {
        console.error("Error submitting KYC documents:", error);
        setFormError("ارسال مدارک با خطا مواجه شد.");
      }
    } finally {
      uploadRequestRef.current = null;
      setActiveUploadField(null);
      setKycSubmitting(false);
    }
  };

  const handleRemoveIdFile = () => {
    if (kycSubmitting) return;
    setIdFile(null);
    setIdUploadProgress(null);
    setFormError("");
  };

  const handleRemoveSelfieFile = () => {
    if (kycSubmitting) return;
    setSelfieFile(null);
    setSelfieUploadProgress(null);
    setFormError("");
  };

  const isVerified =
    localStorage.getItem("role") === "trader_verified" ||
    (identityStatus === "approved" && selfieStatus === "approved");

  const isProfileComplete = !!(
    accountData?.fn &&
    accountData?.ln &&
    accountData?.national_id
  );

  // adjust this depending on your real API shape:
  const hasPendingKyc = identityStatus === "pending" || selfieStatus === "pending";

  const accLoading = profileLoading || kycInitialLoading;

  if (accLoading) {
    return (
      <div className="w-full flex justify-center items-center gap-2">
        <p className="text-[#808080]">در حال بارگذاری</p>
        <TableLoading className="text-[#808080]" />
      </div>
    );
  }

  if (kycError || !hasKycSummary) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-3 text-zi-text-light dark:text-zi-text-dark">
        <p className="text-sm text-red-500">بارگذاری وضعیت احراز هویت با خطا مواجه شد.</p>
        <button type="button" onClick={kycError ? retryKyc : reloadKyc} className="text-sm text-cyan-500">تلاش دوباره</button>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className="w-full flex items-center flex-col gap-10 mt-10">
        <p className="py-2.5 w-11/12 max-w-xs rounded-lg ring-1 ring-zi-text-light dark:ring-zi-text-dark text-center text-lg font-light text-zi-text-light dark:text-zi-text-dark">
          احراز هویت شما تأیید شده است
        </p>
        <SVG name="ilustKYCDone" classname="w-6/12 max-w-xs" />
      </div>
    );
  }

  if (!isProfileComplete) {
    return (
      <div className="w-full flex items-center flex-col gap-10 mt-10">
        <p className="py-2.5 w-11/12 max-w-xs rounded-lg ring-1 ring-zi-text-light dark:ring-zi-text-dark text-center text-lg font-light text-zi-text-light dark:text-zi-text-dark">
          جهت انجام فرایند احراز هویت، ابتدا اطلاعات حساب خود را تکمیل کنید
        </p>
        <SVG name="ilustKYCFillProfile" classname="w-6/12 max-w-xs" />
      </div>
    );
  }

  if (hasPendingKyc) {
    return (
      <div className="w-full flex items-center flex-col gap-10 mt-10">
        <p className="py-2.5 w-11/12 max-w-xs rounded-lg ring-1 ring-zi-text-light dark:ring-zi-text-dark text-center text-lg font-light text-zi-text-light dark:text-zi-text-dark">
          مدارک آپلود شده در حال بررسی است، لطفا منتظر نتیجه بمانید
        </p>
        <SVG name="ilustKYCProcess" classname="w-6/12 max-w-xs" />
      </div>
    );
  }
  return (
    <div className="w-full max-w-5xl rounded-2xl p-4 flex flex-col gap-10 items-center bg-zi-bg2-light dark:bg-zi-bg2-dark">
      <div className="w-full flex flex-col sm:flex-row items-start divide-y sm:divide-y-0 sm:divide-x sm:divide-x-reverse divide-zi-border-light dark:divide-zi-border-dark">
        {/* ======= ID DOCUMENT UPLOAD ======= */}
        <>
          <section className="w-full flex flex-col gap-7 p-5">
            <h3 className="text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark">
              آپلود مدرک شناسایی
            </h3>

            {idFile === null && (
              <label
                className={`w-4/5 h-[138px] self-center rounded-lg flex flex-col justify-center items-center py-5 gap-3 bg-zi-bg1-light dark:bg-zi-bg1-dark ${
                  kycSubmitting
                    ? "cursor-not-allowed opacity-60 pointer-events-none"
                    : "cursor-pointer"
                }`}
              >
                <SVG
                  name="upload"
                  classname="stroke-zi-border-dark dark:stroke-zi-border-light"
                />
                <span className="flex items-center justify-center rounded-full py-1 px-4 border border-zi-border-dark/50 dark:border-zi-border-light/50 text-xs font-normal text-zi-text-light/50 dark:text-zi-text-dark/50">
                  انتخاب فایل
                </span>

                {/* ----------- FILE INPUT ----------- */}
                <input
                  type="file"
                  className="hidden"
                  accept={KYC_FILE_ACCEPT}
                  disabled={kycSubmitting}
                  onChange={(event) =>
                    handleFileChange(event, setIdFile, setIdUploadProgress)
                  }
                />
              </label>
            )}

            <ImageUploadPreviewGrid
              files={idFile ? [idFile] : []}
              onRemove={handleRemoveIdFile}
              uploading={kycSubmitting && activeUploadField === "id"}
              uploadProgress={idUploadProgress ?? 0}
              className="justify-center"
            />

            <div className="text-sm text-justify flex flex-col gap-2.5 text-zi-text-light dark:text-zi-text-dark">
              <p className="text-base font-medium">نکات:</p>
              <p>
                مدارک شناسایی قابل قبول: کارت ملی، گذرنامه، شناسنامه، گواهی‌نامه
              </p>
              <p>فرمت مجاز: JPG ، JPEG ، PNG ، PDF</p>
              <p>حداکثر حجم فایل: ۱۰ مگابایت</p>
            </div>
          </section>
          <section className="w-full flex flex-col gap-7 p-5">
            <h3 className="text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark">
              آپلود تصویر سلفی
            </h3>

            {selfieFile === null && (
              <label
                className={`w-4/5 h-[138px] self-center rounded-lg flex flex-col justify-center items-center py-5 gap-3 bg-zi-bg1-light dark:bg-zi-bg1-dark ${
                  kycSubmitting
                    ? "cursor-not-allowed opacity-60 pointer-events-none"
                    : "cursor-pointer"
                }`}
              >
                <SVG
                  name="upload"
                  classname="stroke-zi-border-dark dark:stroke-zi-border-light"
                />
                <span className="flex items-center justify-center rounded-full py-1 px-4 border border-zi-border-dark/50 dark:border-zi-border-light/50 text-xs font-normal text-zi-text-light/50 dark:text-zi-text-dark/50">
                  انتخاب فایل
                </span>

                {/* ----------- FILE INPUT ----------- */}
                <input
                  type="file"
                  className="hidden"
                  accept={KYC_FILE_ACCEPT}
                  disabled={kycSubmitting}
                  onChange={(event) =>
                    handleFileChange(
                      event,
                      setSelfieFile,
                      setSelfieUploadProgress,
                    )
                  }
                />
              </label>
            )}
            <ImageUploadPreviewGrid
              files={selfieFile ? [selfieFile] : []}
              onRemove={handleRemoveSelfieFile}
              uploading={kycSubmitting && activeUploadField === "selfie"}
              uploadProgress={selfieUploadProgress ?? 0}
              className="justify-center"
            />

            <div className="text-sm text-justify flex flex-col gap-2.5 text-zi-text-light dark:text-zi-text-dark">
              <p className="font-medium text-base">نکات:</p>
              <p>
                مدرک شناسایی آپلود شده را کنار صورت خود قرار داده و یک عکس سلفی
                بگیرید.
              </p>
              <p>عکس باید واضح باشد.</p>
              <p>از پوشیدن عینک و کلاه خودداری نمایید.</p>
              <p>حداکثر حجم فایل: ۱۰ مگابایت</p>
            </div>
          </section>
        </>
      </div>

      {/* Submit Button */}
      {showButton && (
        <div className="w-full max-w-96 flex flex-col gap-3">
          {formError && (
            <p className="text-center text-sm text-red-500">{formError}</p>
          )}

          <div className="w-full flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={kycSubmitting}
              className="flex-1 rounded-xl bg-zi-gradient-button-blue px-4 py-3 text-sm text-zi-text-dark outline-none disabled:opacity-60"
            >
              {kycSubmitting ? "در حال ارسال..." : "ارسال مدارک جهت بررسی"}
            </button>

            {kycSubmitting && (
              <button
                type="button"
                onClick={handleCancelUpload}
                className="rounded-xl border border-zi-border-light dark:border-zi-border-dark px-4 py-3 text-sm text-zi-text-light dark:text-zi-text-dark"
              >
                لغو آپلود
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
